import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { L } from '@lib';
import { InvoicePayment } from '@services';
import { Utils } from '@utils';
import { Alert, Button, Col, Form, FormInstance, Input, Modal, Popconfirm, Row, Typography } from 'antd';
import { PoundOutlined, CreditCardOutlined, InfoCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import { usePaymentTerminal, useCardPayment } from '@hooks';
import { SuccessButton } from '@components';

const { Text } = Typography;

interface IProps {
  paymentForm?: FormInstance<any>;
  modalType?: 'payment' | 'refund' | null;
  invoiceTotal: number;
  totalDue: number;
  paymentTotal?: number;
  handlePaymentCreate?: (values: InvoicePayment) => Promise<void>;
  handleCashRefundCreate?: (values: InvoicePayment) => Promise<void>;
  handlePosPayment?: (values: InvoicePayment) => Promise<void>;
  handlePosRefund?: (values: InvoicePayment) => Promise<void>;
  handlePaymentCancel?: () => void;
  handlePartialPaymentInput?: (value: number) => void;
}

export const InvoicePaymentModal: React.FC<IProps> = observer(
  ({
    paymentForm,
    modalType,
    invoiceTotal,
    totalDue,
    paymentTotal,
    handlePaymentCreate,
    handleCashRefundCreate,
    handlePosPayment,
    handlePosRefund,
    handlePaymentCancel,
    handlePartialPaymentInput,
  }) => {
    const { isTerminalEnabled } = usePaymentTerminal();
    const {
      processingPayment,
      processingStatus,
      cashPaymentLoading,
      cardPaymentLoading,
      hasPaymentError,
      signatureRequired,
      trackingId,
      verifySignature,
    } = useCardPayment();

    const isOpen = modalType !== null;
    const isPaymentMode = modalType === 'payment';
    const isRefundMode = modalType === 'refund';

    const isNegativeBalanceRefund = isRefundMode && totalDue < 0;
    const dueAmount = Utils.roundToTwoDecimals(totalDue);
    const [verifyingSignature, setVerifyingSignature] = useState<'yes' | 'no' | null>(null);

    const getModalTitle = () => {
      switch (modalType) {
        case 'payment':
          return L('Pay invoice');
        case 'refund':
          return L('Process refund');
        default:
          return L('Payment');
      }
    };

    // Dynamic validation based on modal type
    const getAmountValidation = () => {
      // ✅ Skip validation for negative balance refunds (readonly field)
      if (isNegativeBalanceRefund) {
        return [];
      }

      const baseRules = [
        { required: true, message: L('Please enter amount') },
        {
          validator: (_: any, value: number) => {
            if (value === null || value === undefined) {
              return Promise.reject(L('Please enter amount'));
            }

            if (value <= 0) {
              return Promise.reject(L('Amount must be greater than 0'));
            }

            if (isPaymentMode) {
              if (value > dueAmount) return Promise.reject(L('Amount cannot exceed total due'));
              if (value > invoiceTotal)
                return Promise.reject(L('Amount cannot exceed total invoice amount'));
            }

            if (isRefundMode) {
              if (value > (paymentTotal || 0)) {
                return Promise.reject(L('Refund amount cannot exceed paid amount'));
              }
            }

            return Promise.resolve();
          },
        },
      ];
      return baseRules;
    };

    // Dynamic max amount based on modal type
    const getMaxAmount = () => {
      // ✅ For negative balance refunds, use absolute value of totalDue
      if (isNegativeBalanceRefund) {
        return Utils.roundToTwoDecimals(Math.abs(totalDue));
      }

      if (isPaymentMode) return Utils.roundToTwoDecimals(Math.min(dueAmount, invoiceTotal));
      //if (isRefundMode) return Utils.roundToTwoDecimals(paymentTotal || 0);
      if (isRefundMode) return Utils.roundToTwoDecimals(totalDue);
      return Utils.roundToTwoDecimals(invoiceTotal);
    };

    const handleSignatureVerification = useCallback(
      async (isValid: boolean) => {
        if (trackingId) {
          try {
            setVerifyingSignature(isValid ? 'yes' : 'no');
            await verifySignature(trackingId, isValid);
          } catch (error) {
            setVerifyingSignature(null);
          } finally {
            setVerifyingSignature(null);
          }
        }
      },
      [trackingId, verifySignature]
    );

    // Dynamic form initial values based on modal type
    useEffect(() => {
      if (isOpen && paymentForm) {
        let defaultAmount = 0;

        if (isPaymentMode && dueAmount > 0) {
          defaultAmount = dueAmount;
        } else if (isRefundMode) {
          //defaultAmount = totalDue;
          // ✅ Use totalDue for negative balance refunds, otherwise use paymentTotal
          defaultAmount = isNegativeBalanceRefund ? Math.abs(totalDue) : paymentTotal || 0;
        } //else if (isCreditMode && dueAmount > 0) {
        //defaultAmount = dueAmount;
        //}

        const roundedAmount = Utils.roundToTwoDecimals(defaultAmount);

        paymentForm.setFieldsValue({
          amount: roundedAmount,
          note: '',
        });
      }
    }, [
      isOpen,
      paymentForm,
      dueAmount,
      paymentTotal,
      modalType,
      totalDue,
      isNegativeBalanceRefund,
    ]);

    const handleCashPayment = async () => {
      try {
        const values = await paymentForm?.validateFields();
        if (values) {
          const paymentData = {
            ...values,
            method: isRefundMode ? 'CashRefund' : 'Cash',
            type: modalType,
          };

          if (isRefundMode) {
            await handleCashRefundCreate?.(paymentData);
          } else {
            await handlePaymentCreate?.(paymentData);
          }
        }
      } catch (error) {
        console.error('Cash payment failed:', error);
      }
    };

    const handleCardPayment = async () => {
      try {
        const values = await paymentForm?.validateFields();
        if (values) {
          const paymentData = {
            ...values,
            method: isRefundMode ? 'CardRefund' : 'Card',
            type: modalType,
          };

          if (isRefundMode) {
            await handlePosRefund?.(paymentData);
          } else {
            await handlePosPayment?.(paymentData);
          }
        }
      } catch (error) {
        console.error('Card payment failed:', error);
      }
    };

    // Dynamic amount label
    const getAmountLabel = () => {
      if (isRefundMode) return L('Refund Amount');
      return L('Payment Amount');
    };

    const statusMessageComponent = useMemo(() => {
      if (!processingStatus?.terminalMessage) return null;

      return (
        <>
          <Alert message={`EPOS: ${processingStatus.terminalMessage}`} type="info" showIcon />
          <br />
        </>
      );
    }, [processingStatus?.terminalMessage]);

    const eposStatusComponent = useMemo(() => {
      if (!signatureRequired) {
        return (
          <Alert
            type="info"
            message={L(`Card ${modalType} is being processed`)}
            description={
              <>
                <div>
                  {L('Please wait while the transaction completes. Do not close this window.')}
                </div>
                {processingStatus?.statusMessage}
              </>
            }
            showIcon
          />
        );
      }

      return (
        <Alert
          type="warning"
          message={
            <div style={{ textAlign: 'center' }}>{L('Signature Verification Required')}</div>
          }
          description={
            <>
              <Row justify="center" style={{ marginBottom: 16 }}>
                <Col span={24} style={{ textAlign: 'center' }}>
                  <Text strong>{L('Is the Signature on the Receipt Valid?')}</Text>
                </Col>
              </Row>
              <Row justify="center" gutter={12}>
                <Col span={8}>
                  <SuccessButton
                    size="large"
                    block
                    onClick={() => handleSignatureVerification(true)}
                    loading={verifyingSignature === 'yes'}
                    disabled={verifyingSignature !== null}
                  >
                    {L('Yes')}
                  </SuccessButton>
                </Col>
                <Col span={8}>
                  <Button
                    type="primary"
                    danger
                    size="large"
                    block
                    onClick={() => handleSignatureVerification(false)}
                    loading={verifyingSignature === 'no'}
                    disabled={verifyingSignature !== null}
                  >
                    {L('No')}
                  </Button>
                </Col>
              </Row>
            </>
          }
          showIcon={false}
          closable={false}
          style={{ marginTop: 16 }}
        />
      );
    }, [
      signatureRequired,
      handleSignatureVerification,
      modalType,
      verifyingSignature,
      processingStatus?.statusMessage,
    ]);

    return (
      <Modal
        footer={null}
        title={getModalTitle()}
        open={isOpen}
        onCancel={handlePaymentCancel}
        closable={!(cardPaymentLoading && processingPayment)}
        maskClosable={false}
        //maskClosable={!(cardPaymentLoading && processingPayment)} //check if always set to false will do
        width={400}
      >
        {/* Display different summary based on modal type */}
        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Text>{L('Total')}</Text>
          </Col>
          <Col>
            <Text strong>{Utils.formatCurrency(invoiceTotal)}</Text>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Text>{L('Paid')}</Text>
          </Col>
          <Col>
            <Text strong>{Utils.formatCurrency(paymentTotal || 0)}</Text>
          </Col>
        </Row>

        <Row
          justify="space-between"
          style={{ marginBottom: 24, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}
        >
          <Col>
            <Text strong>{isRefundMode ? L('Available for refund') : L('Total due')}</Text>
          </Col>
          <Col>
            <Text strong style={{ color: '#2CBDFF' }}>
              {Utils.formatCurrency(
                // ✅ Show totalDue for negative balance refunds, otherwise use existing logic
                isNegativeBalanceRefund
                  ? Math.abs(totalDue)
                  : isRefundMode
                    ? paymentTotal || 0
                    : dueAmount
              )}
            </Text>
          </Col>
        </Row>

        {/* ✅ Show notice for negative balance refunds */}
        {isNegativeBalanceRefund && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Alert
                type="info"
                // message={L('Overpayment Refund')}
                description={L('Customer has requested a refund. Click to refund by')}
                showIcon
                icon={
                  <InfoCircleFilled
                    style={{
                      fontSize: 32, // Make icon bigger
                      verticalAlign: 'middle', // Center icon vertically
                      // color: '#1890ff'
                    }}
                  />
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              />
            </Col>
          </Row>
        )}

        <Form form={paymentForm} layout="vertical">
          <Form.Item
            name="amount"
            label={getAmountLabel()}
            rules={getAmountValidation()}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Input
              type="number"
              min={0.01}
              max={getMaxAmount()}
              step="0.01"
              prefix="£"
              addonAfter={<PoundOutlined />}
              disabled={isRefundMode}
              readOnly={isRefundMode}
              onChange={(e) => {
                if (isNegativeBalanceRefund) return;

                let value = parseFloat(e.target.value);

                const max = getMaxAmount();
                if (!isNaN(value) && value > max) {
                  value = max;
                  if (paymentForm) {
                    paymentForm.setFieldsValue({ amount: max });
                  }
                }

                if (isNaN(value) || e.target.value === '') {
                  paymentForm?.validateFields(['amount']);
                } else if (value === 0) {
                  paymentForm?.validateFields(['amount']);
                } else {
                  handlePartialPaymentInput?.(value);
                }
              }}
              onWheel={(e) => e.currentTarget.blur()}
              style={{
                backgroundColor: isNegativeBalanceRefund ? '#f5f5f5' : undefined,
                color: isNegativeBalanceRefund ? '#666' : undefined,
              }}
            />
          </Form.Item>

          <Form.Item label={L(`Click to ${isRefundMode ? 'refund' : 'pay'} by:`)}>
            <Row style={{ marginBottom: 12 }}>
              <Col span={24}>
                <Popconfirm
                  title={<span style={{ fontSize: 15, fontWeight: 600 }}>{L(isRefundMode ? 'Process card refund?' : 'Process card payment?')}</span>}
                  description={L(isRefundMode ? 'This will initiate a refund to the card terminal.' : 'This will send the payment to the card terminal.')}
                  onConfirm={handleCardPayment}
                  okText={L('Yes, proceed')}
                  cancelText={L('Cancel')}
                  okButtonProps={{ size: 'middle' }}
                  cancelButtonProps={{ size: 'middle' }}
                  icon={<ExclamationCircleFilled style={{ color: '#faad14', fontSize: 16 }} />}
                  disabled={!isTerminalEnabled || cashPaymentLoading || (cardPaymentLoading && processingPayment)}
                >
                  <SuccessButton
                    icon={<CreditCardOutlined />}
                    size="large"
                    block
                    loading={cardPaymentLoading && processingPayment}
                    disabled={!isTerminalEnabled || cashPaymentLoading}
                    onMouseUp={(e) => e.currentTarget.blur()}
                    style={{
                      height: 56,
                      fontSize: 18,
                      fontWeight: 600,
                    }}
                  >
                    {L('Card')}
                  </SuccessButton>
                </Popconfirm>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Popconfirm
                  title={<span style={{ fontSize: 15, fontWeight: 600 }}>{L(isRefundMode ? 'Process cash refund?' : 'Process cash payment?')}</span>}
                  description={L(isRefundMode ? 'This will record a cash refund for this invoice.' : 'This will record a cash payment for this invoice.')}
                  onConfirm={handleCashPayment}
                  okText={L('Yes, proceed')}
                  cancelText={L('Cancel')}
                  okButtonProps={{ size: 'middle' }}
                  cancelButtonProps={{ size: 'middle' }}
                  icon={<ExclamationCircleFilled style={{ color: '#faad14', fontSize: 16 }} />}
                  disabled={cashPaymentLoading || cardPaymentLoading}
                >
                  <Button
                    type="default"
                    icon={<PoundOutlined />}
                    size="middle"
                    block
                    loading={cashPaymentLoading}
                    disabled={cardPaymentLoading}
                  >
                    {L('Cash')}
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </Form.Item>

          {cardPaymentLoading && processingPayment && (
            <Row style={{ marginTop: 16 }}>
              <Col span={24}>
                {statusMessageComponent}
                {eposStatusComponent}
              </Col>
            </Row>
          )}
          {hasPaymentError && (
            <Row style={{ marginTop: 16 }}>
              <Col span={24}>
                <Alert
                  type="error"
                  message={L('Processing Error')}
                  description={
                    processingStatus?.statusMessage || L('Operation failed. Please try again.')
                  }
                  showIcon
                  closable
                />
              </Col>
            </Row>
          )}
        </Form>
      </Modal>
    );
  }
);
